import client from './client';
import type { ProxyUser, ProxyUserStatus } from '../types';

export interface ProxyUserListResponse {
  total: number;
  unassigned_proxy_count: number;
  items: ProxyUser[];
}

export interface ProxyUserListParams {
  keyword?: string;
  status?: ProxyUserStatus | 'all';
  limit?: number;
  offset?: number;
}

export const listProxyUsers = (params: ProxyUserListParams = {}) =>
  client.get<ProxyUserListResponse>('/api/proxy-users', { params });

export const getProxyUser = (id: number) =>
  client.get<ProxyUser>(`/api/proxy-users/${id}`);

export interface ProxyUserDisconnectCause {
  responsibility: string;
  cause_code: string;
  count: number;
}

export interface ProxyUserDisconnectItem {
  id: number;
  proxy_id: number;
  node_name: string;
  node_ip: string;
  recorded_at: string;
  source_ip: string;
  target_ip: string;
  target_port: number;
  account: string;
  role_name: string;
  responsibility: string;
  cause_code: string;
  confidence: string;
  close_reason: string;
  is_drop?: boolean;
  verdict_text?: string;
}

export interface ProxyUserDisconnectResponse {
  as_of: string;
  window_hours: number;
  total: number;
  causes: ProxyUserDisconnectCause[];
  items: ProxyUserDisconnectItem[];
}

export const getProxyUserDisconnects = (id: number) =>
  client.get<ProxyUserDisconnectResponse>(`/api/proxy-users/${id}/disconnects`);

export interface DisconnectAnalysisNode {
  alias: string;
  peer_users: number;
  peer_users_dropped: number;
  peer_drop_events: number;
  user_drops: number;
  user_drops_with_peer_drop_within_60s: number;
  same_server_peers: number;
  same_server_peers_dropped: number;
}

export interface DisconnectAnalysisHypothesis {
  id: string;
  title: string;
  score: number;
  confidence: 'high' | 'medium' | 'low' | string;
  evidence: string[];
  against: string[];
}

export interface DisconnectAnalysisFacts {
  window_hours: number;
  user_drops: number;
  user_ended_not_drop: number;
  nodes: DisconnectAnalysisNode[];
}

export interface ProxyUserDisconnectAnalysis {
  as_of: string;
  source: 'ai' | 'rules';
  ai_configured: boolean;
  ai_status: 'off' | 'pending' | 'done' | 'error';
  ai_job?: string;
  ai_error?: string;
  summary: string;
  customer_text: string;
  hypotheses: DisconnectAnalysisHypothesis[];
  facts: DisconnectAnalysisFacts;
}

// The rule-based result comes back at once; when a model is configured the
// response carries ai_job and the model's text is fetched by polling it.
export const analyzeProxyUserDisconnects = (id: number, mode?: 'rules') =>
  client.post<ProxyUserDisconnectAnalysis>(`/api/proxy-users/${id}/disconnects/analysis`, null,
    { params: mode ? { mode } : undefined });

export interface DisconnectAnalysisJob {
  ai_status: 'pending' | 'done' | 'error';
  ai_error?: string;
  summary?: string;
  customer_text?: string;
}

export const getProxyUserDisconnectAnalysisJob = (id: number, job: string) =>
  client.get<DisconnectAnalysisJob>(`/api/proxy-users/${id}/disconnects/analysis/${job}`);

export const createProxyUser = (body: { wechat_nickname: string; wechat_id?: string }) =>
  client.post<ProxyUser>('/api/proxy-users', body);

export const patchProxyUser = (
  id: number,
  body: { wechat_nickname?: string; wechat_id?: string; status?: ProxyUserStatus },
) => client.patch<ProxyUser>(`/api/proxy-users/${id}`, body);

export const deleteProxyUser = (id: number) =>
  client.delete(`/api/proxy-users/${id}`);